import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_VIEW_STATE,
  formatViewState,
  parseViewState,
  type ViewState,
} from '@alrimal/contracts';

/**
 * The URL is the state (§1).
 *
 * Not "the URL reflects the state" — it *is* the state. Every visual
 * configuration is a link somebody can forward on WhatsApp and land on, and
 * every change is a back-button stop.
 *
 * Layer toggles and the lighting switch replace the history entry rather than
 * pushing one; opening a unit or a level pushes. Otherwise the back button
 * becomes an undo log for four taps of the same chip.
 */

export interface ViewStateApi {
  state: ViewState;
  set(patch: Partial<ViewState>, options?: { replace?: boolean }): void;
  reset(): void;
}

function currentState(): ViewState {
  if (typeof window === 'undefined') return { ...DEFAULT_VIEW_STATE };
  return parseViewState(window.location.search);
}

export function useViewState(): ViewStateApi {
  const [state, setState] = useState<ViewState>(currentState);
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    const onPopState = () => setState(currentState());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const set = useCallback(
    (patch: Partial<ViewState>, options: { replace?: boolean } = {}) => {
      const next: ViewState = { ...latest.current, ...patch };

      // A unit change invalidates the dwelling and level beneath it. Leaving
      // them would produce `?unit=TH01&dwelling=VL01-A`, which parses to
      // nothing and looks like a bug to whoever received the link.
      if (patch.unit !== undefined && patch.unit !== latest.current.unit) {
        if (patch.dwelling === undefined) next.dwelling = null;
        if (patch.level === undefined) next.level = null;
      }

      const search = formatViewState(next);
      const url = `${window.location.pathname}${search}${window.location.hash}`;

      const replace =
        options.replace ??
        (patch.unit === undefined && patch.level === undefined);

      if (replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);

      latest.current = next;
      setState(next);
    },
    [],
  );

  const reset = useCallback(() => {
    set({ ...DEFAULT_VIEW_STATE }, { replace: false });
  }, [set]);

  return { state, set, reset };
}
